/**
 * Разбор подписанного пропуска (ТЗ FR-PASS-01, FR-PASS-03; ADR-002).
 *
 * Формат QR формирует backend и он единый:
 *   PT1.<kid>.<code>.<base64url(JSON)>.<base64url(HMAC-SHA256)>
 * где JSON = { code, bookingId, enterpriseId, slotId, startsAt, endsAt, guests, expiresAt, kid },
 * а подпись считается по строке `PT1.<kid>.<code>.<base64url(JSON)>`.
 *
 * Идентификатор ключа `kid` присутствует в payload'е — это позволяет ротировать
 * ключи подписи, не выпуская пропуска заново.
 *
 * Если в QR обычный код пропуска (без подписи), разбор возвращает null —
 * такой пропуск проверяется только сервером.
 */

export interface PassClaims {
  /** Код пропуска, например PT-2026-01482. */
  code: string;
  bookingId: string;
  enterpriseId: string;
  slotId: string;
  startsAt: string;
  endsAt: string;
  /** Количество гостей по одному пропуску. */
  guests: number;
  /** Нормализованное поле для интерфейса. */
  guestCount: number;
  expiresAt: string;
  /** Идентификатор ключа подписи. */
  kid: string;
}

export interface SignedPass {
  claims: PassClaims;
  /** Подпись в base64url. */
  signature: string;
  /** Подписываемая часть: PT1.<kid>.<code>.<payload>. */
  signedPart: string;
}

export const PASS_PREFIX = 'PT1';

export function base64UrlToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeClaims(signedPart: string, kid: string, code: string): PassClaims | null {
  try {
    const json = new TextDecoder().decode(base64UrlToBytes(signedPart));
    const parsed = JSON.parse(json) as Partial<PassClaims>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.enterpriseId !== 'string' || typeof parsed.expiresAt !== 'string') return null;
    const guests = typeof parsed.guests === 'number' ? parsed.guests : 0;
    return {
      code: typeof parsed.code === 'string' ? parsed.code : code,
      bookingId: parsed.bookingId ?? '',
      enterpriseId: parsed.enterpriseId,
      slotId: parsed.slotId ?? '',
      startsAt: parsed.startsAt ?? '',
      endsAt: parsed.endsAt ?? '',
      guests,
      guestCount: guests,
      expiresAt: parsed.expiresAt,
      kid: typeof parsed.kid === 'string' ? parsed.kid : kid,
    };
  } catch {
    return null;
  }
}

/**
 * Разбор QR пропуска. Поддерживается только канонический формат backend'а —
 * это исключает неоднозначность при проверке подписи.
 */
export function parseSignedPass(raw: string): SignedPass | null {
  const text = raw.trim();
  if (!text) return null;

  const parts = text.split('.');
  if (parts.length !== 5 || parts[0] !== PASS_PREFIX) return null;

  const kid = parts[1] ?? '';
  const code = parts[2] ?? '';
  const payload = parts[3] ?? '';
  const signature = parts[4] ?? '';
  if (!kid || !code || !payload || !signature) return null;

  const claims = decodeClaims(payload, kid, code);
  if (!claims) return null;

  return {
    claims,
    signature,
    signedPart: `${PASS_PREFIX}.${kid}.${code}.${payload}`,
  };
}

/** Человекочитаемый код пропуска, если он есть внутри payload'а. */
export function extractPassCode(raw: string): string {
  const parsed = parseSignedPass(raw);
  if (parsed) {
    return parsed.claims.code || raw;
  }
  return raw.trim();
}
